import { MeInformation } from '@api'
import { context, propagation, trace } from '@opentelemetry/api'
import {
  AxiosError,
  AxiosHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'

import { queryClient } from '@/consts/query'

import { OTEL_DEPLOYMENT_ENVIRONMENT } from './consts'
import { extractNamespace } from './utils'

const tracer = trace.getTracer('axios-instrumentation')

export const otelRequestInterceptor = (config: InternalAxiosRequestConfig) => {
  const user = queryClient.getQueryState<MeInformation>(['account'])
  const namespace = extractNamespace(config.url)

  const span = tracer.startSpan(
    `HTTP ${config.method?.toUpperCase() || 'REQUEST'} (${namespace})`,
    {
      attributes: {
        'http.method': config.method,
        'http.url': config.url,
        'span.kind': 'client',
      },
    },
  )

  span.setAttribute('deployment.environment', OTEL_DEPLOYMENT_ENVIRONMENT)

  span.addEvent('open')
  const headers = new AxiosHeaders(config.headers)

  // Only inject traceparent if the span is actually being sampled
  if (span.isRecording()) {
    const spanContext = trace.setSpan(context.active(), span)
    const headers = new AxiosHeaders(config.headers)

    propagation.inject(spanContext, headers)
    config.headers = headers
  }

  if (user) {
    headers.set('x-user-id', user.data?.userId.toString())
  }

  // Attach span to config for later
  config.otelSpan = span

  span.addEvent('send')

  return config
}

export const otelResponseInterceptor = (response: AxiosResponse) => {
  const span = response.config.otelSpan

  if (span) {
    span.addEvent('responseEnd', { status: response.status })
    span.setAttribute('http.status_code', response.status)
    span.end()
  }

  return response
}

export const otelErrorInterceptor = (error: AxiosError) => {
  const span = error.config?.otelSpan

  if (span) {
    span.addEvent('error', { message: error.message, code: error.code })
    span.setAttribute('http.status_code', error.response?.status || 0)
    span.recordException(error)
    span.setStatus({ code: 2, message: error.message }) // 2 = ERROR
    span.end()
  }

  return Promise.reject(error)
}
